import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * How long to wait for a variantUpdate before assuming the variant picker's section fetch
 * failed. Generous on purpose: a slow-but-successful fetch must not trip this, and every new
 * option click restarts the clock. Only a fetch that never resolves reaches it.
 */
const VARIANT_UPDATE_TIMEOUT = 10000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string | undefined, quantity: number, properties?: Record<string, string>}>} */
  #addToCartQueue = [];

  /** @type {string | undefined} The variant clicked in the in-flight variant change, if any. */
  #pendingVariantId;

  /** @type {number | undefined} Fires if the in-flight variant change never reports back. */
  #variantUpdateWatchdog;

  /**
   * @type {boolean} Whether #recoverFromFailedVariantUpdate is the reason the add-to-cart
   * buttons are disabled. Tracked so that disable can be undone symmetrically and ONLY where
   * this class applied it — a blanket enable() would override a button the server legitimately
   * rendered as Sold out.
   */
  #watchdogDisabledAddToCart = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    clearTimeout(this.#variantUpdateWatchdog);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      // Queue against the variant the shopper actually clicked. It may be undefined when the
      // option value has no direct variant — #processBatchAddToCart resolves those from the
      // form input once the section fetch has written the server-resolved variant to it.
      this.#addToCartQueue.push({
        variantId: this.#pendingVariantId,
        quantity: this.#getQuantity(),
        // Captured now, not at flush time, so the queued item carries exactly what the shopper
        // had filled in when they clicked.
        properties: this.#collectLineItemProperties(),
      });

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Snapshots the form's line-item properties for a queued add.
   *
   * The direct path posts `new FormData(form)`, which carries every field in the form.
   * #processBatchAddToCart cannot — the AJAX API needs a JSON `items` array — so it builds its
   * payload by hand and previously sent only id and quantity. Anything else the shopper had
   * entered was dropped: a gift card queued during a variant change reached the cart with no
   * recipient email, name, message or send date, and nothing anywhere reported the loss.
   *
   * Reading through FormData rather than the DOM keeps this consistent with the direct path —
   * disabled inputs are skipped the same way (the gift-card timezone offset is disabled until
   * the recipient form is shown).
   *
   * @returns {Record<string, string> | undefined} Properties by name, or undefined if none.
   */
  #collectLineItemProperties() {
    const form = this.querySelector('form');
    if (!form) return undefined;

    /** @type {Record<string, string>} */
    const properties = {};

    for (const [name, value] of new FormData(form).entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }

    return Object.keys(properties).length > 0 ? properties : undefined;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          // Announce regardless of whether this form renders inline error text. Quick-add
          // cards have no addToCartTextError ref, and this is their only signal that the
          // add failed — previously an early return here discarded the error entirely.
          this.#setLiveRegionText(response.message);

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              const newTextNode = document.createTextNode(response.message);
              addToCartTextError.appendChild(newTextNode);
            }

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');

              // Clear the announcement
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          } else {
            this.#timeout = setTimeout(() => this.#clearLiveRegionText(), ERROR_MESSAGE_DISPLAY_DURATION);
          }

          // Undo the optimistic "Added" state — the button animates on click, before the
          // server answers, so a rejected add otherwise leaves a success checkmark sitting
          // over an item that never entered the cart.
          for (const container of allAddToCartContainers) {
            container.refs.addToCartButton?.removeAttribute('data-added');
          }

          // Always dispatched, including for forms with no inline error element (quick-add
          // cards) — the early return this replaced skipped it entirely for those, so nothing
          // downstream ever learned the add failed. Consumers MUST check detail.data.didError
          // before treating this as a successful add; cart-icon.js and cart-drawer.js were
          // given that guard alongside this change, and quick-add.js already had it.
          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              // Stated explicitly rather than left to sourceId. This path happens to pass the
              // variant there, but #processBatchAddToCart passes the component id instead, so
              // a consumer reading sourceId gets the variant from one path and not the other.
              variantId: id.toString(),
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        this.#handleAddToCartFailure(error, {
          containers: allAddToCartContainers,
          addToCartTextError,
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1,
        });
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Handles an add that never produced a usable server response: a rejected fetch (offline,
   * dropped mobile connection) or a body that is not JSON (a CDN 502/504 HTML error page).
   *
   * Both add paths previously bottomed out in `console.error` and nothing else. Because the
   * button animates optimistically at click time, that left the FULL success state — checkmark
   * burst plus the product image flying into the cart icon — playing over an add that never
   * happened, then clearing itself on its own 800ms timer exactly as a real add does. Verified
   * live against the storefront: the only difference from a genuine add was that the badge
   * never moved, and for screen-reader users there was no signal whatsoever.
   *
   * This mirrors what the HTTP-error branch above already does — roll back the optimistic
   * state, say so, and dispatch didError so the other surfaces can unwind too. The message is
   * the shared products.product.add_to_cart_error string rather than a hardcoded one, so it
   * stays translated; #recoverFromFailedVariantUpdate's comment about there being no such
   * string is what this key was added for.
   *
   * @param {unknown} error
   * @param {object} context
   * @param {NodeListOf<AddToCartComponent>} context.containers
   * @param {HTMLElement | undefined} [context.addToCartTextError]
   * @param {number} context.itemCount
   */
  #handleAddToCartFailure(error, { containers, addToCartTextError, itemCount }) {
    console.error(error);

    // Undo the optimistic "Added" state on every button this form owns. Deliberately ABOVE the
    // abort check below: an aborted add did not reach the cart either, so leaving the checkmark
    // up would restore the phantom-success state this method exists to remove. Neither add
    // fetch passes a signal today, so nothing aborts — but that is one `signal:` away from
    // being true, and the ordering is the part that would be wrong when it happens.
    for (const container of containers) {
      container.refs.addToCartButton?.removeAttribute('data-added');
    }

    // A superseded request is not a failure the shopper caused, so it gets the rollback but no
    // message and no event — a newer add is already in flight and will report for itself.
    if (error instanceof DOMException && error.name === 'AbortError') return;

    const message = Theme.translations.add_to_cart_error;

    if (message) {
      // Announced unconditionally: quick-add cards render no inline error text, so for them
      // this is the only channel. Same reasoning as the HTTP-error branch above.
      this.#setLiveRegionText(message);

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');

        // childNodes[2] is the text node after the icon span — reused rather than appended so
        // repeated failures do not stack duplicate messages inside the same element.
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(message));
        }
      }

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        addToCartTextError?.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    // Dispatched as cartUpdate with didError, which is what cart-icon.js, cart-drawer.js,
    // quick-add.js and sticky-add-to-cart.js already branch on — so the badge stays put, the
    // drawer stays shut, and the sticky bar rolls back its own animation and clears its
    // stuck data-puppet flag. Previously no event was dispatched at all on this path.
    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount,
        productId: this.dataset.productId,
      })
    );
  }

  /** @param {Array<{variantId: string | undefined, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    // Runs after #onVariantUpdate has written the server-resolved variant to the form input,
    // so that is the correct fallback for items queued without one. Anything still unresolved
    // is dropped rather than guessed — adding the wrong variant is worse than adding nothing.
    const resolvedVariantId = this.refs.variantId?.value;
    items = items
      .map((item) => ({ ...item, variantId: item.variantId || resolvedVariantId }))
      .filter((item) => item.variantId);

    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        // Carried through from queue time. Without this the hand-built payload silently drops
        // every line-item property the direct FormData path would have sent.
        ...(item.properties ? { properties: item.properties } : {}),
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          // Announced regardless of whether this form renders inline error text — quick-add
          // has no addToCartTextError ref, and this is its only signal. Mirrors the same fix
          // in #processAddToCart; this sibling path was missed the first time.
          this.#setLiveRegionText(response.message);

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          } else {
            this.#timeout = setTimeout(() => this.#clearLiveRegionText(), ERROR_MESSAGE_DISPLAY_DURATION);
          }

          // Undo the optimistic "Added" state. handleSubmit animates at QUEUE time, before any
          // round-trip, so a rejected batch add otherwise leaves a success checkmark over items
          // that never reached the cart — the same rollback #processAddToCart already does.
          for (const container of this.querySelectorAll('add-to-cart-component')) {
            /** @type {AddToCartComponent} */ (container).refs.addToCartButton?.removeAttribute('data-added');
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            // Last of the flushed queue: these are adds the shopper made during a variant
            // change, so the most recent one is the variant they were looking at.
            variantId: items[items.length - 1]?.variantId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        // Same treatment as the direct path — this sibling has been the one missed every time.
        this.#handleAddToCartFailure(error, {
          containers: /** @type {NodeListOf<AddToCartComponent>} */ (
            this.querySelectorAll('add-to-cart-component')
          ),
          addToCartTextError,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        });
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    // Re-sync data-cart-quantity for the newly selected variant (nudge). The section
    // morph preserves the quantity input's existing attributes, so without
    // this the previous variant's (or page-load-time) cart count leaks into
    // the new variant's max-quantity validation and blocks legitimate adds
    // ("This item has a maximum of N" with none of that variant in the cart).
    this.#fetchAndUpdateCartQuantity();

    // The update landed, so the failure watchdog started in #onVariantSelected is moot.
    clearTimeout(this.#variantUpdateWatchdog);
    this.#variantUpdateWatchdog = undefined;

    this.#variantChangeInProgress = false;
    this.#pendingVariantId = undefined;

    // Unfreeze accelerated checkout — the form now carries the resolved variant. Cleared here
    // rather than alongside the hidden/available handling below, which sits behind an early
    // return that would otherwise leave the button permanently inert.
    this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('inert');

    // Likewise released before that early return, and before the availability check below so
    // that check gets the final say on the container it owns.
    this.#releaseWatchdogDisable();

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      // Mirror the server's decision instead of recomputing from the variant object. The
      // section render already applied buy-buttons' full can_add_to_cart chain — including the
      // quantity-rule sold-out case, where the variant still has stock and so reports
      // available: true. Deriving from `resource.available` alone re-showed the payment button
      // for a variant the very same response rendered as Sold out, letting a shopper buy it in
      // one tap. Falls back to the old check only if the response has no such container.
      const newAccelerated = event.detail.data.html.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      const hide =
        newAccelerated != null
          ? newAccelerated.hasAttribute('hidden')
          : event.detail.resource == null || event.detail.resource.available == false;

      if (hide) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    this.#variantChangeInProgress = true;
    this.#pendingVariantId = event.detail.resource?.variantId;

    // The accelerated checkout button posts the form's hidden variant id, which still holds
    // the previous selection until #onVariantUpdate lands — clicking it in this window would
    // check the shopper out on the variant they just switched away from. Freeze it until the
    // variant resolves; #onVariantUpdate clears this.
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('inert', '');

    // variant-picker's fetch can end without ever dispatching variantUpdate: a network
    // failure hits its .catch, and a response missing the variant JSON returns early. Nothing
    // else clears the state set above, so the accelerated checkout would stay inert forever
    // and every later add would silently join a queue that is never flushed — each one still
    // playing the success animation. Restarted on every option click, so rapid switching (and
    // the AbortErrors it causes) just defers it; only a fetch that never lands gets here.
    clearTimeout(this.#variantUpdateWatchdog);
    this.#variantUpdateWatchdog = setTimeout(() => this.#recoverFromFailedVariantUpdate(), VARIANT_UPDATE_TIMEOUT);
  };

  /**
   * Unwinds the in-progress variant change when no variantUpdate ever arrived.
   *
   * Queued adds are DROPPED rather than submitted: without the update the hidden input still
   * holds the previous variant, so sending them would add the variant the shopper switched
   * away from — the exact mis-purchase the queue exists to prevent.
   */
  #recoverFromFailedVariantUpdate() {
    const pendingVariantId = this.#pendingVariantId;
    const { variantId } = this.refs;

    // Resync the form to the option the shopper actually clicked, BEFORE anything is released.
    // Releasing without this is what made the recovery worse than the hang: the hidden input
    // still held the PREVIOUS selection, so unfreezing handed back a live Add to cart and a
    // live Shop Pay button both wired to the variant they had just switched away from, while
    // the swatch, the price and the URL all showed the new one. Verified live against the
    // storefront — choosing M on a stalled connection put Wine / XS in the cart, silently.
    const resynced = Boolean(pendingVariantId) && Boolean(variantId);
    if (resynced && variantId.value !== pendingVariantId) {
      variantId.value = /** @type {string} */ (pendingVariantId);
    }

    this.#variantChangeInProgress = false;
    this.#pendingVariantId = undefined;

    if (resynced) {
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('inert');

      // A previous firing may have disabled the buttons on the !resynced path below. Releasing
      // `inert` without also undoing that left express checkout live while Add to cart stayed
      // dead — two stalled variant changes in a row on one page produced exactly that.
      this.#releaseWatchdogDisable();
    } else {
      // VariantSelectedEvent omits variantId when the clicked option value maps to no single
      // variant (a colour with no variant in the selected size). There is nothing to resync
      // to, so rather than release a form pointing at the wrong variant, keep the accelerated
      // checkout frozen and take the add-to-cart buttons out of service too — #processAddToCart
      // returns early while any of them is disabled, which closes the direct path as well as
      // the express one.
      //
      // Flagged rather than fire-and-forget: #onVariantUpdate's own enable() only covers
      // refs.addToCartButtonContainer, so it cannot by itself undo a disable applied to every
      // container here. #releaseWatchdogDisable is the matching un-set, and both it and
      // #onVariantUpdate call it before that method's availability check.
      for (const container of this.querySelectorAll('add-to-cart-component')) {
        /** @type {AddToCartComponent} */ (container).disable?.();
      }
      this.#watchdogDisabledAddToCart = true;
    }

    const abandoned = this.#addToCartQueue.splice(0);

    // Those adds already played the optimistic success animation at queue time. Roll it back
    // rather than leaving a checkmark over items that never reached the cart.
    if (abandoned.length > 0) {
      for (const container of this.querySelectorAll('add-to-cart-component')) {
        /** @type {AddToCartComponent} */ (container).refs.addToCartButton?.removeAttribute('data-added');
      }
    }

    // Announced whenever the shopper has been left with something to make sense of — adds
    // dropped, OR the buttons just taken out of service. This used to sit below an
    // `if (abandoned.length === 0) return`, so the commonest case (a stalled variant change
    // with nothing queued) greyed out every Add to cart button and said nothing at all.
    if (abandoned.length > 0 || !resynced) {
      const { addToCartTextError } = this.refs;
      const message = Theme.translations.add_to_cart_error;

      if (message) {
        this.#setLiveRegionText(message);

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
        }

        // Hides the inline error as well as clearing the announcement. The previous timer only
        // did the latter, so when this cleared a pending hide scheduled by the 422 branch the
        // visible error text it replaced was never taken back down.
        if (this.#timeout) clearTimeout(this.#timeout);
        this.#timeout = setTimeout(() => {
          addToCartTextError?.classList.add('hidden');
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }
    }

    // Only dispatched when adds were actually dropped — didError tells other surfaces to unwind
    // an add, and there is no add to unwind when the queue was empty.
    if (abandoned.length === 0) return;

    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: abandoned.reduce((sum, item) => sum + item.quantity, 0),
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * Re-enables add-to-cart buttons that #recoverFromFailedVariantUpdate disabled, and only
   * those. Gated on the flag rather than enabling unconditionally: the buttons may also be
   * disabled because the server rendered the variant as Sold out, and clearing that would hand
   * the shopper a live button for something they cannot buy.
   */
  #releaseWatchdogDisable() {
    if (!this.#watchdogDisabledAddToCart) return;
    this.#watchdogDisabledAddToCart = false;

    for (const container of this.querySelectorAll('add-to-cart-component')) {
      /** @type {AddToCartComponent} */ (container).enable?.();
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
